import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { track } from "../lib/mixpanel"

// Rates by year (Kloepfer K13-26, Larry's Oct 27 call): Settings → Timesheets
// → Codes & Lists → Rates. One page for a year's equipment, materials and
// labor rates: a 2025 | 2026 | 2027 switch, and each report uses the rates of
// its own year. Equipment is priced by category (the machines' Equipment
// type): one rate for the category, each machine its own, or both — a
// machine's own rate wins. Click a category to see its machines. A year with
// no rates yet uses the last year's until "Start 2027 from 2026" or its first
// change copies them. "Group with AI" proposes categories for the machines
// in a pop-up; nothing is saved until the office checks it. Every save
// answers with the whole year again (/timesheets/rates), so nothing reloads.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])

const money = (v) => (v === null || v === undefined ? "" : Number(v).toFixed(2))

const CHEVRON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 transition-transform" aria-hidden="true"><path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clip-rule="evenodd"/></svg>'

const INPUT = "w-24 px-2 py-1 border rounded-lg text-[13px] text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
const TH = "px-2 py-1.5 font-medium whitespace-nowrap"
const TD = "px-2 py-1.5 whitespace-nowrap"
const BTN = "px-3 py-1.5 rounded-lg text-[13px] cursor-pointer whitespace-nowrap"

export default class extends Controller {
  static targets = ["pane"]
  static values = { url: String, copyUrl: String, groupUrl: String, applyUrl: String, text: Object }

  connect() {
    this.year = new Date().getFullYear()
    this.payload = null
    this.opened = new Set() // categories showing their machines
    // Opened straight on Rates (…#rates): the tab was clicked before this controller was listening.
    queueMicrotask(() => { if (this.hasPaneTarget && !this.paneTarget.hidden) this.show() })
  }

  t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => o?.[k], this.textValue) ?? ""
    if (typeof s === "object") s = (vars.count === 1 ? s.one : s.other) ?? ""
    for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`%{${k}}`, v)
    return s
  }

  async show() {
    if (!this.hasPaneTarget) return
    if (!this.payload) {
      if (this._loading) return
      this._loading = true
      this.paneTarget.innerHTML = `<p class="text-[13px] text-gray-400">${esc(this.t("loading"))}</p>`
      const data = await this._request("GET", `${this.urlValue}?year=${this.year}`)
      this._loading = false
      if (!data) return
      this.payload = data
      track("Rates Viewed", { year: data.year, has_own_rates: data.own, machines: data.machines_total })
    }
    this._render()
  }

  async pickYear(e) {
    const data = await this._request("GET", `${this.urlValue}?year=${e.currentTarget.dataset.year}`)
    if (!data) return
    this.year = data.year
    this.payload = data
    this._render()
  }

  toggle(e) {
    const id = e.currentTarget.dataset.category
    if (this.opened.has(id)) this.opened.delete(id)
    else this.opened.add(id)
    this._render()
  }

  // A rate typed in a box: saved on change (Enter or leaving the box). Blank clears it.
  async save(e) {
    const el = e.currentTarget
    const raw = el.value.trim().replace(/[$,]/g, "")
    if (raw !== "" && !(Number(raw) >= 0)) {
      el.classList.add("border-red-400")
      showToast(this.t("invalid"), "alert")
      return
    }
    await this._saveRate(el.dataset.kind, el.dataset.target, raw)
  }

  // A machine back on its category's rate.
  async useCategory(e) {
    await this._saveRate("equipment", e.currentTarget.dataset.target, "")
  }

  async copyYear(e) {
    const { from, to } = e.currentTarget.dataset
    const data = await this._request("POST", this.copyUrlValue, { from, to })
    if (!data) return
    this.year = data.year
    this.payload = data
    this._render()
    showToast(this.t("copied", { count: data.copied, from, to }))
  }

  saveOnEnter(e) {
    if (e.key === "Enter") e.currentTarget.blur()
  }

  // ── Group with AI ──────────────────────────────────────────────────

  async groupWithAi(e) {
    const btn = e.currentTarget
    btn.disabled = true
    btn.textContent = this.t("ai_reading")
    const data = await this._request("POST", this.groupUrlValue, {})
    btn.disabled = false
    btn.textContent = this.t("ai_button")
    if (!data) return
    if (!data.groups.length) {
      showToast(this.t("ai_nothing"), "alert")
      return
    }
    this.grouping = data
    this.choice = {}
    data.groups.forEach((g) => g.machines.forEach((m) => { this.choice[m.id] = g.name }))
    data.unplaced.forEach((m) => { this.choice[m.id] = "" })
    this.groupOpened = new Set()
    this._renderGroup()
  }

  toggleGroup(e) {
    const name = e.currentTarget.dataset.group
    if (this.groupOpened.has(name)) this.groupOpened.delete(name)
    else this.groupOpened.add(name)
    this._renderGroup()
  }

  pickGroup(e) {
    this.choice[e.currentTarget.dataset.machine] = e.currentTarget.value
  }

  closeGroup() {
    if (this.modal) this.modal.innerHTML = ""
    this.grouping = null
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  async saveGroups(e) {
    const assign = Object.fromEntries(Object.entries(this.choice).filter(([, name]) => name))
    e.currentTarget.disabled = true
    const data = await this._request("POST", this.applyUrlValue, { year: this.year, assign })
    if (!data) {
      e.currentTarget.disabled = false
      return
    }
    this.closeGroup()
    this.payload = data
    this._render()
    showToast(this.t("ai_saved", { added: data.added, moved: data.moved }))
  }

  // ── Drawing ────────────────────────────────────────────────────────

  _render() {
    const p = this.payload
    if (!p) return
    // Keep the box the person tabbed into after a save.
    const focused = document.activeElement?.dataset?.rateKey
    const next = p.year + 1
    const years = p.years.map((y) => `<button type="button" data-action="click->time-rates#pickYear" data-year="${y}" aria-pressed="${y === p.year}"
        class="px-3 py-1 rounded-md cursor-pointer ${y === p.year ? "bg-brand text-white font-semibold" : "text-gray-600 hover:bg-gray-100"}">${y}</button>`).join("")
    const startNext = p.own && !p.own_years.includes(next)
      ? `<button type="button" data-action="click->time-rates#copyYear" data-from="${p.year}" data-to="${next}" class="${BTN} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">${esc(this.t("start_from", { to: next, from: p.year }))}</button>` : ""
    const borrowed = !p.own && p.from
      ? `<div class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-rates-borrowed>
           <span class="text-[13px] text-amber-900">${esc(this.t("borrowed", { year: p.year, from: p.from }))}</span>
           <button type="button" data-action="click->time-rates#copyYear" data-from="${p.from}" data-to="${p.year}" class="${BTN} border border-amber-300 bg-white text-amber-900 hover:bg-amber-100">${esc(this.t("start_from", { to: p.year, from: p.from }))}</button>
         </div>` : ""

    this.paneTarget.innerHTML = `
      <div class="space-y-4" data-rates-year="${p.year}">
        <div>
          <h4 class="text-[15px] font-semibold text-gray-900">${esc(this.t("title"))}</h4>
          <p class="text-[13px] text-gray-500">${esc(this.t("help"))}</p>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-wrap items-center gap-3">
            <div class="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 text-[13px]" role="group" aria-label="${esc(this.t("year"))}">${years}</div>
            <span class="text-[13px] text-gray-500">${esc(this.t("own_year_note", { year: p.year, next }))}</span>
          </div>
          ${startNext}
        </div>
        ${borrowed}
        <div class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-purple-300 bg-purple-50 px-3 py-2">
          <div class="min-w-0">
            <div class="text-[13px] font-semibold text-purple-700">${esc(this.t("ai_title"))}</div>
            <div class="text-[13px] text-purple-900/80">${esc(this.t("ai_help", { count: p.machines_total }))}</div>
          </div>
          <button type="button" data-action="click->time-rates#groupWithAi" class="${BTN} bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-60">${esc(this.t("ai_button"))}</button>
        </div>
        ${this._equipmentTable(p)}
        <div class="grid gap-6 lg:grid-cols-[3fr_2fr]">
          ${this._materialsTable(p)}
          ${this._laborTable(p)}
        </div>
      </div>`
    if (focused) this.paneTarget.querySelector(`[data-rate-key="${focused}"]`)?.focus()
  }

  _input(kind, target, value, { placeholder = "", own = false, label = "" } = {}) {
    const look = own ? "border-blue-500 text-blue-700 font-semibold bg-white" : (value === null || value === undefined) && placeholder ? "border-dashed border-gray-300 bg-gray-50 text-gray-500" : "border-gray-300 bg-white"
    return `<input type="text" inputmode="decimal" value="${esc(money(value))}" placeholder="${esc(placeholder || "0.00")}" aria-label="${esc(label)}"
      data-kind="${kind}" data-target="${target}" data-rate-key="${kind}:${target}"
      data-action="change->time-rates#save keydown->time-rates#saveOnEnter" class="${INPUT} ${look}">`
  }

  _equipmentTable(p) {
    const rows = []
    const groups = [...p.categories]
    if (p.untyped.length) groups.push({ id: "none", name: this.t("no_category"), rate: null, machines: p.untyped, untyped: true })
    groups.forEach((c) => {
      const open = this.opened.has(String(c.id))
      const own = c.machines.filter((m) => m.rate !== null).length
      const ownChip = own ? `<span class="rounded-full bg-blue-50 px-2 text-[13px] text-blue-700">${esc(this.t("own_rates", { count: own }))}</span>` : `<span class="text-gray-400">—</span>`
      const rate = c.untyped ? `<span class="text-gray-400">—</span>` : this._input("equipment_type", c.id, c.rate, { label: this.t("rate_of", { name: c.name }) })
      rows.push(`<tr class="${open ? "bg-orange-50/60" : ""}" data-rates-category="${c.id}">
        <td class="${TD}">
          <button type="button" data-action="click->time-rates#toggle" data-category="${c.id}" aria-expanded="${open}" class="inline-flex items-center gap-1.5 cursor-pointer text-gray-900 hover:text-brand"${c.machines.length ? "" : " disabled"}>
            <span class="${open ? "rotate-90" : ""} inline-flex text-gray-400">${CHEVRON}</span>
            ${c.code && c.code !== c.name ? `<span class="tabular-nums text-gray-500">${esc(c.code)}</span>` : ""}<b class="font-semibold">${esc(c.name)}</b>
          </button>
        </td>
        <td class="${TD} tabular-nums text-gray-600">${c.machines.length}</td>
        <td class="${TD} text-right">${rate}</td>
        <td class="${TD}">${ownChip}</td>
      </tr>`)
      if (!open) return
      c.machines.forEach((m) => {
        const hasOwn = m.rate !== null
        const action = hasOwn && !c.untyped
          ? `<button type="button" data-action="click->time-rates#useCategory" data-target="${m.id}" class="text-brand hover:underline cursor-pointer">${esc(this.t("use_category", { name: c.name }))}</button>`
          : `<span class="text-gray-500">${esc(c.untyped ? (hasOwn ? this.t("own_rate") : this.t("no_rate")) : this.t("uses_category", { name: c.name }))}</span>`
        rows.push(`<tr class="bg-gray-50/70" data-rates-machine="${m.id}">
          <td class="${TD} pl-10 text-gray-700">${esc(m.label)}</td>
          <td class="${TD}"></td>
          <td class="${TD} text-right">${this._input("equipment", m.id, m.rate, { own: hasOwn, placeholder: c.untyped ? "" : money(c.rate), label: this.t("rate_of", { name: m.label }) })}</td>
          <td class="${TD} text-[13px]">${action}</td>
        </tr>`)
      })
    })
    const empty = groups.length ? "" : `<tr><td colspan="4" class="px-2 py-6 text-center text-[13px] text-gray-500">${esc(this.t("no_equipment"))}</td></tr>`
    return `<div class="space-y-1.5">
      <h5 class="text-[13px] font-semibold text-gray-900">${esc(this.t("equipment_title"))}</h5>
      <p class="text-[13px] text-gray-500">${esc(this.t("equipment_help"))}</p>
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-[13px]">
          <thead class="bg-gray-50 text-gray-500"><tr>
            <th class="${TH} text-left w-1/2">${esc(this.t("col_category"))}</th><th class="${TH} text-left">${esc(this.t("col_machines"))}</th>
            <th class="${TH} text-right">${esc(this.t("col_rate", { year: p.year }))}</th><th class="${TH} text-left">${esc(this.t("col_own"))}</th>
          </tr></thead>
          <tbody class="divide-y divide-gray-200">${rows.join("")}${empty}</tbody>
        </table>
      </div>
    </div>`
  }

  _materialsTable(p) {
    const rows = p.materials.map((m) => `<tr data-rates-material="${m.id}">
      <td class="${TD} text-gray-900">${m.code && m.code !== m.name ? `<span class="tabular-nums text-gray-500 mr-1.5">${esc(m.code)}</span>` : ""}${esc(m.name)}</td>
      <td class="${TD} text-gray-600">${esc(m.unit || "")}</td>
      <td class="${TD} text-right">${this._input("material", m.id, m.rate, { label: this.t("rate_of", { name: m.name }) })}</td>
    </tr>`).join("") || `<tr><td colspan="3" class="px-2 py-6 text-center text-[13px] text-gray-500">${esc(this.t("no_materials"))}</td></tr>`
    return `<div class="space-y-1.5">
      <h5 class="text-[13px] font-semibold text-gray-900">${esc(this.t("materials_title"))}</h5>
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-[13px]">
          <thead class="bg-gray-50 text-gray-500"><tr><th class="${TH} text-left">${esc(this.t("col_material"))}</th><th class="${TH} text-left">${esc(this.t("col_unit"))}</th><th class="${TH} text-right">${esc(this.t("col_rate", { year: p.year }))}</th></tr></thead>
          <tbody class="divide-y divide-gray-200">${rows}</tbody>
        </table>
      </div>
    </div>`
  }

  _laborTable(p) {
    return `<div class="space-y-1.5">
      <h5 class="text-[13px] font-semibold text-gray-900">${esc(this.t("labor_title"))}</h5>
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-[13px]">
          <thead class="bg-gray-50 text-gray-500"><tr><th class="${TH} text-left">${esc(this.t("col_labor"))}</th><th class="${TH} text-right">${esc(this.t("col_rate", { year: p.year }))}</th></tr></thead>
          <tbody><tr data-rates-labor><td class="${TD} text-gray-900">${esc(this.t("labor_everyone"))}</td><td class="${TD} text-right">${this._input("labor", 0, p.labor, { label: this.t("labor_title") })}</td></tr></tbody>
        </table>
      </div>
      <p class="text-[13px] text-gray-500">${esc(this.t("labor_help"))}</p>
    </div>`
  }

  // The AI's groups, to check before anything is saved: each machine can move
  // to another category or be left out; "Needs a look" are the ones it couldn't place.
  _renderGroup() {
    const g = this.grouping
    if (!g) return
    if (!this.modal) {
      this.modal = document.createElement("div")
      this.paneTarget.after(this.modal)
    }
    const scroll = this.modal.querySelector("[data-rates-group-body]")?.scrollTop || 0
    const names = g.groups.map((x) => x.name)
    const options = (picked) => [`<option value="" ${picked ? "" : "selected"}>${esc(this.t("ai_leave_out"))}</option>`]
      .concat(names.map((n) => `<option value="${esc(n)}" ${n === picked ? "selected" : ""}>${esc(n)}</option>`)).join("")
    const machineRows = (list) => list.map((m) => `<tr class="bg-gray-50/70">
        <td class="${TD} pl-8 text-gray-700">${esc(m.label)}${m.type ? ` <span class="text-gray-400">· ${esc(m.type)}</span>` : ""}</td>
        <td class="${TD}" colspan="3"><select data-action="change->time-rates#pickGroup" data-machine="${m.id}" aria-label="${esc(this.t("ai_category_for", { name: m.label }))}"
          class="py-1 pl-2 pr-7 border border-gray-300 rounded-lg text-[13px] bg-white text-gray-700">${options(this.choice[m.id])}</select></td>
      </tr>`).join("")
    const groupRow = (name, list, chip, warn = false) => {
      const open = this.groupOpened.has(name)
      const examples = list.slice(0, 3).map((m) => m.label).join(", ") + (list.length > 3 ? " …" : "")
      return `<tr class="${warn ? "bg-orange-50" : ""}">
          <td class="${TD}"><b class="font-semibold ${warn ? "text-orange-800" : "text-gray-900"}">${esc(warn ? this.t("ai_needs_look") : name)}</b> ${chip}</td>
          <td class="${TD} tabular-nums text-gray-600">${list.length}</td>
          <td class="${TD} text-gray-500 max-w-[260px] truncate">${esc(warn ? this.t("ai_unknown") : examples)}</td>
          <td class="${TD} text-right"><button type="button" data-action="click->time-rates#toggleGroup" data-group="${esc(name)}" class="text-brand hover:underline cursor-pointer">${esc(open ? this.t("ai_hide") : (warn ? this.t("ai_place") : this.t("ai_see_all")))}</button></td>
        </tr>${open ? machineRows(list) : ""}`
    }
    const placed = g.groups.reduce((n, x) => n + x.machines.length, 0)
    const rows = g.groups.map((x) => groupRow(x.name, x.machines, x.type_id
      ? `<span class="rounded-full bg-green-50 px-2 text-[13px] text-green-700">${esc(this.t("ai_existing"))}</span>`
      : `<span class="rounded-full bg-purple-50 px-2 text-[13px] text-purple-700">${esc(this.t("ai_new"))}</span>`)).join("")
      + (g.unplaced.length ? groupRow("__unplaced", g.unplaced, "", true) : "")
    this.modal.innerHTML = `
      <div class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-action="click->time-rates#closeGroup" role="dialog" aria-modal="true" aria-labelledby="ts-rates-ai-title">
        <div class="w-full max-w-3xl max-h-[88vh] flex flex-col rounded-xl bg-white shadow-xl" data-action="click->time-rates#stopPropagation">
          <div class="px-5 pt-5 pb-3 border-b border-gray-200">
            <h3 id="ts-rates-ai-title" class="text-base font-semibold text-gray-900">${esc(this.t("ai_result", { placed, total: g.total, count: g.groups.length }))}</h3>
            <p class="mt-1 text-[13px] text-gray-500">${esc(this.t("ai_result_help"))}</p>
          </div>
          <div class="flex-1 min-h-0 overflow-y-auto px-5 py-2" data-rates-group-body>
            <table class="w-full text-[13px]">
              <thead class="text-gray-500"><tr><th class="${TH} text-left">${esc(this.t("col_category"))}</th><th class="${TH} text-left">${esc(this.t("col_machines"))}</th><th class="${TH} text-left">${esc(this.t("ai_examples"))}</th><th class="${TH}"></th></tr></thead>
              <tbody class="divide-y divide-gray-200">${rows}</tbody>
            </table>
          </div>
          <div class="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
            <button type="button" data-action="click->time-rates#closeGroup" class="${BTN} border border-gray-300 text-gray-700 hover:bg-gray-50">${esc(this.t("cancel"))}</button>
            <button type="button" data-action="click->time-rates#saveGroups" class="${BTN} bg-brand text-white font-semibold hover:bg-brand-dark disabled:opacity-60">${esc(this.t("ai_save"))}</button>
          </div>
        </div>
      </div>`
    const body = this.modal.querySelector("[data-rates-group-body]")
    if (body) body.scrollTop = scroll
  }

  async _saveRate(kind, target, rate) {
    const data = await this._request("PATCH", this.urlValue, { year: this.payload.year, kind, target_id: target, rate })
    if (!data) return
    this.payload = data
    this._render()
    showToast(this.t(rate === "" ? "cleared" : "saved"))
  }

  async _request(method, url, body) {
    const headers = { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Accept": "application/json" }
    if (body) headers["Content-Type"] = "application/json"
    try {
      const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined, credentials: "same-origin" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showToast(data.error || this.t("error"), "alert")
        return null
      }
      return data
    } catch (_) {
      showToast(this.t("error"), "alert")
      return null
    }
  }
}
